import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Popover from '@mui/material/Popover';
import Typography from '@mui/material/Typography';
import {type NotificationDto} from '@onroadvantage/optix-safeandsound-openapi';

import {NotificationList} from './NotificationList';

interface NotificationsPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  count: number;
  notifications: NotificationDto[];
  loading: boolean;
  isError: boolean;
  isMarkingAllRead: boolean;
  onClose: () => void;
  onMarkAllRead: () => void;
  onArchive: (id: string) => void;
  onNotificationClick: (notification: NotificationDto) => void;
}

interface NotificationsPopoverBodyProps {
  notifications: NotificationDto[];
  loading: boolean;
  isError: boolean;
  onArchive: (id: string) => void;
  onNotificationClick: (notification: NotificationDto) => void;
}

function NotificationsLoadingState(): JSX.Element {
  return (
    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
      <CircularProgress size={28} />
    </Box>
  );
}

function NotificationsEmptyState(): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        py: 4,
      }}
    >
      <NotificationsNoneIcon color="disabled" fontSize="large" />
      <Typography variant="body2" color="text.secondary">
        You&apos;re all caught up
      </Typography>
    </Box>
  );
}

function NotificationsErrorState(): JSX.Element {
  return (
    <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
      <Typography variant="body2" color="text.secondary">
        Unable to load notifications
      </Typography>
    </Box>
  );
}

function NotificationsPopoverBody({
  notifications,
  loading,
  isError,
  onArchive,
  onNotificationClick,
}: NotificationsPopoverBodyProps): JSX.Element {
  if (loading) {
    return <NotificationsLoadingState />;
  }
  if (isError) {
    return <NotificationsErrorState />;
  }
  if (notifications.length === 0) {
    return <NotificationsEmptyState />;
  }
  return (
    <NotificationList
      notifications={notifications}
      onNotificationClick={onNotificationClick}
      onArchive={onArchive}
    />
  );
}

export function NotificationsPopover({
  anchorEl,
  open,
  count,
  notifications,
  loading,
  isError,
  isMarkingAllRead,
  onClose,
  onMarkAllRead,
  onArchive,
  onNotificationClick,
}: NotificationsPopoverProps): JSX.Element {
  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
      transformOrigin={{vertical: 'top', horizontal: 'right'}}
      slotProps={{paper: {sx: {width: 380, maxWidth: '100%'}}}}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
        }}
      >
        <Typography variant="subtitle1">Notifications</Typography>
        <Button
          size="small"
          disabled={count === 0 || isMarkingAllRead}
          onClick={onMarkAllRead}
        >
          Mark all as read
        </Button>
      </Box>
      <Divider />
      <NotificationsPopoverBody
        notifications={notifications}
        loading={loading}
        isError={isError}
        onArchive={onArchive}
        onNotificationClick={onNotificationClick}
      />
    </Popover>
  );
}
